import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../config";

const techSuggestions = [
  "React",
  "Angular",
  "Vue",
  "Node.js",
  "Next.js",
  "Python",
  "Django",
  "Flask",
  "Java",
  "Spring Boot",
  "AWS",
  "Docker",
  "Kubernetes",
  "MongoDB",
  "PostgreSQL",
];

const Add_tech_company = () => {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [techStack, setTechStack] = useState([]);
  const [startups, setStartups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingStartups, setLoadingStartups] = useState(true);

  const [formData, setFormData] = useState({
    startupId: "",
    companyName: "",
    website: "",
    email: "",
    phone: "",
    companyType: "",
    industry: "",
    companySize: "",
    city: "",
    hiringStatus: "Hiring",
    status: "Active",
  });

  useEffect(() => {
    const fetchStartups = async () => {
      try {
        setLoadingStartups(true);
        const res = await apiClient.get("/api/admin_routes/startups/all?limit=1000");
        const list = res.data.startups || [];
        setStartups(list);
        if (list.length > 0) {
          setFormData((prev) => ({ ...prev, startupId: list[0]._id }));
        }
      } catch (err) {
        console.error("Error loading startups:", err);
        toast.error("Failed to load startups list");
      } finally {
        setLoadingStartups(false);
      }
    };
    fetchStartups();
  }, []);

  const addTech = (tech) => {
    if (!tech) return;
    if (techStack.includes(tech)) return;

    setTechStack([...techStack, tech]);
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTech(input.trim());
    }

    if (e.key === "Backspace" && !input && techStack.length) {
      setTechStack(techStack.slice(0, -1));
    }
  };

  const removeTech = (tech) => {
    setTechStack(techStack.filter((t) => t !== tech));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.startupId) {
      toast.error("Please select a startup first");
      return;
    }

    const billingAddress = [
      `Website: ${formData.website || "N/A"}`,
      `Type: ${formData.companyType || "N/A"}`,
      `Industry: ${formData.industry || "N/A"}`,
      `Size: ${formData.companySize || "N/A"}`,
      `Hiring: ${formData.hiringStatus}`,
      `Status: ${formData.status}`,
      techStack.length > 0 ? `Tech Stack: ${techStack.join(", ")}` : "",
    ].filter(Boolean).join(" | ");

    const payload = {
      vendorName: formData.companyName.trim(),
      vendorType: "software",
      vendorContact: formData.companyName.trim(),
      vendorEmail: formData.email.trim(),
      vendorPhone: formData.phone.trim(),
      vendorCountry: "India",
      vendorState: formData.city.trim() || "Delhi",
      vendorTaxId: "",
      vendorBillingAddress: billingAddress,
    };

    try {
      setSubmitting(true);
      const res = await apiClient.post(`/api/vendors/${formData.startupId}`, payload);
      if (res.data.success) {
        toast.success("Tech Company added successfully!");
        navigate("/dashboard");
      } else {
        throw new Error(res.data.message || "Failed to save Tech Company");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save Tech Company");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header border-bottom bg-transparent py-3">
              <h5 className="mb-0 fw-semibold text-dark">Add Tech Company</h5>
            </div>
            <div className="card-body p-4">
              <form className="row g-3" onSubmit={handleSubmit}>
                {/* Select Startup */}
                <div className="col-12">
                  <label className="form-label text-secondary">Link to Startup *</label>
                  {loadingStartups ? (
                    <div className="text-muted small">Loading startups...</div>
                  ) : (
                    <select
                      className="form-select"
                      name="startupId"
                      value={formData.startupId}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Startup</option>
                      {startups.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.companyName || s.founderName || s.email}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Company Name */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Company Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    placeholder="Enter company name"
                    required
                  />
                </div>

                {/* Website */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Website</label>
                  <input
                    type="url"
                    className="form-control"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://company.com"
                  />
                </div>

                {/* Email */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Official Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="info@company.com"
                    required
                  />
                </div>

                {/* Phone */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Official Phone Number *</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                  />
                </div>

                {/* Company Type */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Company Type</label>
                  <select
                    className="form-select"
                    name="companyType"
                    value={formData.companyType}
                    onChange={handleInputChange}
                  >
                    <option value="">Select type</option>
                    <option>Startup</option>
                    <option>Product</option>
                    <option>Service</option>
                    <option>Enterprise</option>
                    <option>Agency</option>
                  </select>
                </div>

                {/* Industry */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Industry / Domain</label>
                  <select
                    className="form-select"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                  >
                    <option value="">Select domain</option>
                    <option>SaaS</option>
                    <option>FinTech</option>
                    <option>EdTech</option>
                    <option>HealthTech</option>
                    <option>E-commerce</option>
                    <option>AI / ML</option>
                  </select>
                </div>

                {/* Company Size */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Company Size</label>
                  <select
                    className="form-select"
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleInputChange}
                  >
                    <option value="">Select size</option>
                    <option>1–10</option>
                    <option>11–50</option>
                    <option>51–200</option>
                    <option>201–500</option>
                    <option>500+</option>
                  </select>
                </div>

                {/* City */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">City *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                    required
                  />
                </div>

                {/* Tech Stack */}
                <div className="col-12">
                  <label className="form-label text-secondary">Tech Stack</label>

                  <div className="form-control d-flex flex-wrap gap-2 p-2">
                    {techStack.map((tech) => (
                      <span
                        key={tech}
                        className="badge bg-success d-flex align-items-center"
                      >
                        {tech}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          onClick={() => removeTech(tech)}
                        />
                      </span>
                    ))}

                    <input
                      type="text"
                      className="border-0 flex-grow-1"
                      placeholder="Type tech and press Enter"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      list="tech-suggestions"
                      style={{ outline: "none" }}
                    />
                  </div>

                  <datalist id="tech-suggestions">
                    {techSuggestions.map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>

                  <small className="text-muted">
                    Press Enter or comma to add · Backspace to remove last
                  </small>
                </div>

                {/* Hiring Status */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Hiring Status</label>
                  <select
                    className="form-select"
                    name="hiringStatus"
                    value={formData.hiringStatus}
                    onChange={handleInputChange}
                  >
                    <option>Hiring</option>
                    <option>Not Hiring</option>
                  </select>
                </div>

                {/* Status */}
                <div className="col-12 col-md-6">
                  <label className="form-label text-secondary">Status</label>
                  <select
                    className="form-select"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </div>

                {/* Submit */}
                <div className="col-12 d-flex justify-content-end gap-2 mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate(-1)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={submitting}
                  >
                    {submitting ? "Saving..." : "Save Company"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Add_tech_company;
